import { User } from 'firebase/auth'
import { Timestamp } from 'firebase/firestore'
import { UserRole } from './userRoles'
import { UserType } from './userTypes.types'
import { UploadedFile } from './storage.types'

export interface UserDetails extends User {
  // Make UserFirebaseData fields optional since they might not be available during auth
  loginEmail?: string
  role?: UserRole
  type?: UserType[]
  memberSince?: Timestamp
  profileCompletedAt?: Timestamp
  subscriptionStatus?: SubscriptionStatus
  firstName?: string
  lastName?: string
  jobTitle?: string
  companyName?: string
  companyWebsite?: string
  linkedinUrl?: string
  contactEmail?: string
  contactMobile?: string
  contactPhone?: string
  bio?: string
  workingIn?: sector[]
  interestedIn?: interest[]
  latestThread?: string
  profilePicture?: UploadedFile
  createdAt?: Timestamp
  lastLoginAt?: Timestamp
}

// AccountProfileFormData represents the form data submitted by the user
export type AccountProfileFormData = {
  firstName: string
  lastName: string
  jobTitle: string
  companyName: string
  companyWebsite?: string
  linkedinUrl?: string
  contactEmail: string
  contactMobile?: string
  contactPhone?: string
  bio: string
  workingIn: sector[]
  interestedIn: interest[]
  latestThread?: string
  profilePicture?: File
}

// UserFirebaseData represents the data stored in Firestore, which includes AccountProfileFormData
export type UserFirebaseData = Omit<AccountProfileFormData, 'profilePicture'> & {
  loginEmail?: string
  role?: UserRole
  type?: UserType[]
  memberSince?: Timestamp
  profileCompletedAt?: Timestamp
  subscriptionStatus?: SubscriptionStatus
  createdAt?: Timestamp
  lastLoginAt?: Timestamp
  profilePicture?: UploadedFile
}

// Types for inviteNewMember function (old system)
export interface InviteNewMemberInput {
  loginEmail?: string // For backwards compatibility
  email?: string // New field name for custom invitation system
  firstName?: string
  lastName?: string
  type?: UserType[]
  memberSince?: string
  expirationDays?: number // Optional: defaults to 7 days in custom system
}

export interface InviteNewMemberOutput {
  data: {
    // Old system fields
    userId?: string
    message: string
    email: string
    // New custom system fields
    tokenId?: string
    invitationLink?: string
    expiresAt?: string
  }
}

export type SubscriptionStatusType = SubscriptionStatus.ACTIVE | SubscriptionStatus.UNPAID | SubscriptionStatus.DEACTIVATED

export enum SubscriptionStatus {
  ACTIVE = 'active',
  UNPAID = 'unpaid',
  DEACTIVATED = 'deactivated',
}

export enum Sectors {
  AUDIT_SERVICES = 'auditServices',
  AI = 'ai',
  AGRICULTURE = 'agriculture',
  AGRIBUSINESS = 'agribusiness',
  ARTS = 'arts',
  AUTOMOTIVE = 'automotive',
  BANKING = 'banking',
  CONSTRUCTION = 'construction',
  CONSULTING = 'consulting',
  EDUCATION = 'education',
  ENERGY = 'energy',
  ENTERTAINMENT = 'entertainment',
  ENVIRONMENT = 'environment',
  EVENTS = 'events',
  FILM_TV_PRODUCTION = 'filmTvProduction',
  HEALTH = 'health',
  HOSPITALITY = 'hospitality',
  HUMAN_RESOURCES = 'humanResources',
  INFORMATION_TECHNOLOGY = 'informationTechnology',
  INSURANCE = 'insurance',
  LEGAL = 'legal',
  MARKETING = 'marketing',
  MEDICINE = 'medicine',
  NON_PROFIT = 'nonProfit',
  PUBLIC_SECTOR = 'publicSector',
  PRIVATE_EQUITY = 'privateEquity',
  PSYCHOLOGY = 'psychology',
  REAL_ESTATE = 'realEstate',
  SECURITY = 'security',
  SHIPPING = 'shipping',
  SPORTS = 'sports',
  TELECOMMUNICATIONS = 'telecommunications',
  TRANSPORTATION = 'transportation',
  TOURISM = 'tourism',
  TRADE = 'trade',
  INDUSTRY = 'industry',
}
export enum Interests {
  NETWORKING = 'networking',
  NEW_BUSINESS_RELATIONS = 'relations',
  RECEIVING_INVITES = 'invites',
  SPONSORING = 'sponsoring',
  HELPING_GROW = 'grow',
}

export type sector =
  | Sectors.AUDIT_SERVICES
  | Sectors.AI
  | Sectors.BANKING
  | Sectors.HEALTH
  | Sectors.AGRICULTURE
  | Sectors.AGRIBUSINESS
  | Sectors.ARTS
  | Sectors.AUTOMOTIVE
  | Sectors.CONSTRUCTION
  | Sectors.CONSULTING
  | Sectors.EDUCATION
  | Sectors.ENERGY
  | Sectors.ENTERTAINMENT
  | Sectors.ENVIRONMENT
  | Sectors.EVENTS
  | Sectors.FILM_TV_PRODUCTION
  | Sectors.HOSPITALITY
  | Sectors.HUMAN_RESOURCES
  | Sectors.INFORMATION_TECHNOLOGY
  | Sectors.INSURANCE
  | Sectors.LEGAL
  | Sectors.MARKETING
  | Sectors.MEDICINE
  | Sectors.NON_PROFIT
  | Sectors.PUBLIC_SECTOR
  | Sectors.PRIVATE_EQUITY
  | Sectors.PSYCHOLOGY
  | Sectors.REAL_ESTATE
  | Sectors.SECURITY
  | Sectors.SHIPPING
  | Sectors.SPORTS
  | Sectors.TELECOMMUNICATIONS
  | Sectors.TOURISM
  | Sectors.TRADE
  | Sectors.INDUSTRY

export type interest =
  | Interests.NETWORKING
  | Interests.NEW_BUSINESS_RELATIONS
  | Interests.RECEIVING_INVITES
  | Interests.SPONSORING
  | Interests.HELPING_GROW
